import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Zap, Heart, Building2, MessageSquare } from "lucide-react";
import { WhatsAppIcon, GoogleGIcon } from "@/components/oneyork/icons";
import { BRAND, IMAGES, waLink, smsLink } from "@/lib/oneyork";

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?client=safari&hs=f3NV&hl=en-ca&sca_esv=3b4cc2d9bc5e80fb&sxsrf=ANbL-n6b_9xdjd1Ux186YlOIeE45TA0KGw:1781130880661&q=one+york+auto+detailing+(+1+york+)+reviews&uds=ALYpb_lqRuhHOn9shXMP1aHnx5OhAfWZpM8Y-qg7F-bVZjL_3QlK_43co2OpZxQRmYwR4fnH49FXI7n6_zvo2TJcsUYmLhA-vGCnRQbgNpfFNaTAht6MeO6Py1Be9zh0HtCIaigbxQIDrzA9YrA1kCjSETIgkJPirrEBxqdNMk5cn06_K9D7nvuyU1db2JC9ZS3TvjuJCRBPjWugH1rDdvuplkAOofsxJR-ub9E8NBm3ckJZnYAF-M5Vi1bHqzaaH7PQ0YO6p0-GUIWknH7ub2OiK1VT3SG_9Z95Fm38uia0pJVo7S6z0fZVlmtnUoPyVA9ugkF2NjZnaVkICkZifUiQNHhPVGfKvNdxIAcXcuHRFGgQNDh35djhZBhN3XoVsHrN98Rw5wFZia_rMtaQcvxCW3invDlPUOHDitBW4iawerenmZslxmBy5YJpqQcrdjAuynvnLA553d2L5XGvGKA6b4SFmeLqNaOqiUdZc5nrFeVMl4iXdWMzQud36MMoE_C5vXlbKkM65pB6Ke6GnRpEAPim6WTOlt5DUjijTD4jIownx8Qg30U&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOWbqqUTz4uSl-IF00kS7jWCdkNm2uMBfiG8ozuqUuvXXiBpiXAtBPGXHrnokJ6e_mwYEWJksIWvq-YFQgeJiZdNlt8s9xTn3bQfxdEAWFJAIY4vaa82LyVF6YqQF7zIHecIKw98%3D&sa=X&ved=2ahUKEwiGv8LL3f2UAxWJFTQIHT1MHe4Qk8gLegQIFxAB&ictx=1&stq=1&cs=1&lei=gOYpaob_J4mr0PEPvZj18A4#ebo=4";

const HERO_BADGES = [
  { icon: Star, label: "5.0 Rated On Google & Verified Account" },
  { icon: Zap, label: "Fast Response For Quotes" },
  { icon: Heart, label: "Kids & Pets Friendly Products" },
  { icon: Building2, label: "Residential & Commercial" },
];

export default function Hero() {
  const sectionRef = useRef(null);
  // Scroll progress through the hero section (0 = top in view, 1 = hero scrolled off top)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  // Image translates DOWN as the page scrolls → text/buttons glide UP over it faster than the image moves.
  // Starts lifted up so the car bumper aligns with the first bullet point.
  const imageY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative bg-[#050505] overflow-hidden"
    >
      {/* ============ MOBILE LAYOUT (text overlays the image) ============ */}
      <div className="lg:hidden relative w-full min-h-[100svh] overflow-hidden">
        {/* Full image backdrop — parallax scroll on top of intro fade */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 z-0"
        >
          <motion.div
            style={{ y: imageY, willChange: "transform" }}
            className="absolute inset-0"
          >
            <img
              src={IMAGES.hero}
              alt="ONE YORK branded vehicle"
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </motion.div>
          {/* Vertical gradient — softer in the middle so the car is visible */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/30 to-black/90 pointer-events-none" />
        </motion.div>

        {/* Content overlay */}
        <div className="relative z-10 flex flex-col min-h-[100svh] px-5 sm:px-7 pt-24 pb-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <span className="block w-8 h-px bg-[#d4af37]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37] font-medium">
                {BRAND.full}
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display font-extrabold text-white leading-[0.95] tracking-tighter drop-shadow-[0_2px_18px_rgba(0,0,0,0.7)]"
            >
              <span className="block text-[30px] xs:text-[34px] sm:text-[42px] font-bold whitespace-nowrap">
                Premium Mobile Detailing &amp;
              </span>
              <span className="block text-[#d4af37] text-[42px] xs:text-5xl sm:text-[58px] font-extrabold mt-1">
                Ceramic Coating
              </span>
              <span className="block text-xl sm:text-2xl font-medium tracking-tight text-white/90 mt-2">
                in York Region / GTA.
              </span>
            </h1>
          </motion.div>

          {/* Spacer pushes badges to bottom */}
          <div className="flex-1" />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="grid grid-cols-1 gap-2.5 mb-3"
          >
            {HERO_BADGES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 text-[13px] text-white/90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)]"
              >
                <Icon
                  size={15}
                  strokeWidth={1.75}
                  className="text-[#d4af37] shrink-0"
                />
                <span>{label}</span>
              </div>
            ))}
          </motion.div>

          {/* Mobile CTA buttons — WhatsApp left, Text Message right (side-by-side) */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="flex gap-2.5"
          >
            <a
              data-testid="hero-whatsapp-btn-mobile"
              href={waLink("Hi One York,")}
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#25D366] hover:bg-[#1DA851] transition-colors whitespace-nowrap"
            >
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#dc2626] text-white text-[8px] font-semibold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full shadow-md">
                Best Way
              </span>
              <WhatsAppIcon width={15} height={15} />
              WhatsApp Chat
            </a>
            <a
              data-testid="hero-sms-btn-mobile"
              href={smsLink("Hi One York,")}
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full text-white text-[13px] font-semibold transition-colors whitespace-nowrap"
              style={{
                border: "1px solid rgba(255,255,255,0.28)",
                background:
                  "linear-gradient(140deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.45), 0 6px 18px -6px rgba(0,0,0,0.45)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
              }}
            >
              <MessageSquare size={14} strokeWidth={1.8} />
              Text Message
            </a>
          </motion.div>

          {/* Mobile reviews CTA */}
          <motion.a
            data-testid="hero-reviews-btn-mobile"
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-2.5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#d4af37] hover:bg-[#f2c94c] text-black text-[13px] font-semibold transition-colors whitespace-nowrap shadow-[0_10px_24px_-10px_rgba(212,175,55,0.65)]"
          >
            <GoogleGIcon size={16} />
            Check What The Public Says
          </motion.a>
        </div>
      </div>

      {/* ============ DESKTOP LAYOUT (text overlaps un-distorted image) ============ */}
      <div className="hidden lg:block relative min-h-screen bg-[#000000]">
        {/* Image — anchored right, slightly enlarged so it covers more rightward area */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="absolute top-0 right-0 bottom-0 w-[76%] xl:w-[72%] overflow-hidden"
        >
          <motion.div
            style={{ y: imageY, willChange: "transform" }}
            className="absolute inset-0"
          >
            <img
              src={IMAGES.hero}
              alt="ONE YORK branded vehicle on open road"
              className="w-full h-full object-contain object-right"
              style={{
                transform: "scale(1.3)",
                transformOrigin: "right center",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, transparent 18%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,1) 65%)",
                maskImage:
                  "linear-gradient(to right, transparent 0%, transparent 18%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,1) 65%)",
              }}
              loading="eager"
            />
          </motion.div>
          {/* Soft top/bottom vignette */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#000000]/70 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#000000]/80 to-transparent pointer-events-none" />
        </motion.div>

        {/* Text — overlaps onto the image */}
        <div className="relative z-10 grid grid-cols-12 min-h-screen items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-8 xl:col-span-7 pl-16 xl:pl-28 2xl:pl-36 pr-10 pt-28 pb-16"
          >
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-8">
                <span className="block w-12 h-px bg-[#d4af37]" />
                <span className="text-[13px] uppercase tracking-[0.28em] text-[#d4af37] font-medium">
                  {BRAND.full} · Certified Ceramic Coating Installer
                </span>
              </div>

              <p
                data-testid="hero-headline-desktop"
                className="font-display font-extrabold text-white leading-[0.95] tracking-tighter drop-shadow-[0_2px_24px_rgba(0,0,0,0.75)]"
              >
                <span className="block text-3xl xl:text-4xl 2xl:text-5xl font-bold whitespace-nowrap">
                  Premium Mobile Detailing &amp;
                </span>
                <span className="block text-[#d4af37] text-5xl xl:text-[58px] 2xl:text-[77px] font-extrabold mt-2">
                  Ceramic Coating
                </span>
                <span className="block text-2xl xl:text-3xl font-medium tracking-tight text-white/90 mt-3">
                  in York Region / GTA.
                </span>
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3.5 max-w-xl">
                {HERO_BADGES.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 text-[14px] text-white/85 drop-shadow-[0_1px_10px_rgba(0,0,0,0.7)]"
                  >
                    <Icon
                      size={16}
                      strokeWidth={1.75}
                      className="text-[#d4af37] shrink-0"
                    />
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {/* Hero CTA buttons */}
              <div className="mt-8 flex flex-wrap gap-3 sm:gap-4">
                <a
                  data-testid="hero-whatsapp-btn"
                  href={waLink("Hi One York,")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full text-white font-semibold bg-[#25D366] hover:bg-[#1DA851] transition-colors"
                >
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#dc2626] text-white text-[9px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full shadow-md">
                    Best Way
                  </span>
                  <WhatsAppIcon width={18} height={18} />
                  WhatsApp Chat
                </a>
                <a
                  data-testid="hero-sms-btn"
                  href={smsLink("Hi One York,")}
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full text-white font-semibold transition-colors"
                  style={{
                    border: "1px solid rgba(255,255,255,0.28)",
                    background:
                      "linear-gradient(140deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.45), 0 6px 18px -6px rgba(0,0,0,0.45)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                  }}
                >
                  <MessageSquare size={16} strokeWidth={1.8} />
                  Text Message
                </a>
                <a
                  data-testid="hero-reviews-btn"
                  href={GOOGLE_REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full bg-[#d4af37] hover:bg-[#f2c94c] text-black font-semibold transition-colors shadow-[0_12px_30px_-12px_rgba(212,175,55,0.65)]"
                >
                  <GoogleGIcon size={18} />
                  Check What The Public Says
                </a>
              </div>

              <div className="mt-8 flex items-center gap-6 text-xs uppercase tracking-[0.24em] text-white/55">
                <span>FEYNLAB Certified</span>
                <span className="w-px h-3 bg-white/25" />
                <span>CARFAX Registered</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
